<template>
  <v-container class="identity-page px-6 px-md-10 py-8">
    <v-alert
      v-if="error"
      type="error"
      outlined
      class="mb-6"
    >
      {{ error }}
    </v-alert>

    <div v-if="loading">
      <v-skeleton-loader type="card, card, card, card" class="mb-8" />
      <v-skeleton-loader type="list-item-three-line, list-item-three-line" />
    </div>

    <div v-else-if="report" class="report-view">
      <div class="report-top-actions mb-6">
        <v-btn outlined class="report-nav-button" @click="handleBackToQueries">
          <v-icon left small>mdi-arrow-left</v-icon>
          Volver a consultas
        </v-btn>
      </div>

      <div class="report-header mb-8">
        <div>
          <div class="report-title-line">
            <h1>{{ report.subject.name }}</h1>
            <v-chip small label outlined class="entity-chip">
              {{ report.subject.type }}
            </v-chip>
          </div>
          <p>RFC/CURP: {{ report.subject.identifier }} • Generado: {{ report.generatedAt }}</p>
          <v-chip
            v-if="report.checks.length > 0 && report.status !== 'completed'"
            small
            label
            color="#FFF3CD"
            text-color="#8A6D3B"
            class="progress-chip mt-2"
          >
            <v-icon left x-small color="#8A6D3B">mdi-progress-clock</v-icon>
            Actualizando — {{ report.progress.done }}/{{ report.progress.total }} fuentes completadas
          </v-chip>
        </div>

        <div class="report-actions">
          <v-btn
            v-if="report.checks.length > 0 && report.status !== 'completed'"
            outlined
            class="report-nav-button"
            @click="handleRefreshReport"
          >
            <v-icon left small>mdi-refresh</v-icon>
            Actualizar
          </v-btn>
          <v-btn outlined class="report-nav-button" @click="handleShareReport">
            <v-icon left small>mdi-share-variant-outline</v-icon>
            Compartir
          </v-btn>
          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn outlined class="report-nav-button" :loading="downloadingPdf" v-bind="attrs" v-on="on">
                <v-icon left small>mdi-download-outline</v-icon>
                Descargar PDF
                <v-icon right small>mdi-chevron-down</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item @click="handleDownloadReport('resumen')">
                <v-list-item-icon><v-icon small>mdi-file-outline</v-icon></v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>Resumen</v-list-item-title>
                  <v-list-item-subtitle>Score, indicadores y hallazgos principales</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
              <v-list-item @click="handleDownloadReport('extenso')">
                <v-list-item-icon><v-icon small>mdi-file-document-multiple-outline</v-icon></v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>Extenso</v-list-item-title>
                  <v-list-item-subtitle>Incluye el detalle completo de cada fuente consultada</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </div>

      <section class="mb-10">
        <v-row>
          <v-col cols="12" lg="8">
            <v-card class="risk-score-card pa-7" elevation="2">
              <div class="risk-card-top">
                <div>
                  <p>Score de Riesgo</p>
                  <div class="risk-score">
                    {{ report.riskScore }}
                    <span>/10</span>
                  </div>
                  <v-chip small label color="rgba(255, 255, 255, 0.22)" text-color="white" class="risk-level-chip">
                    {{ report.riskLevelLabel }}
                  </v-chip>
                </div>

                <div class="risk-shield">
                  <v-icon color="white" size="58">mdi-shield-outline</v-icon>
                </div>
              </div>

              <div
                v-for="indicator in report.riskIndicators"
                :key="indicator.label"
                class="risk-meter"
              >
                <div>
                  <span>{{ indicator.label }}</span>
                  <strong v-if="indicator.value != null">{{ indicator.value }}/10</strong>
                  <strong v-else class="no-data">Sin datos suficientes</strong>
                </div>
                <v-progress-linear
                  v-if="indicator.value != null"
                  :value="indicator.value * 10"
                  height="11"
                  rounded
                  background-color="rgba(255, 255, 255, 0.25)"
                  color="#050417"
                />
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" lg="4">
            <v-card
              class="recommendation-card pa-7"
              :class="recommendationMeta.cardClass"
              elevation="2"
            >
              <div class="recommendation-icon">
                <v-icon :color="recommendationMeta.color">
                  {{ recommendationMeta.icon }}
                </v-icon>
              </div>

              <h2>{{ recommendationMeta.title }}</h2>
              <p>{{ recommendationMeta.description }}</p>
            </v-card>
          </v-col>
        </v-row>
      </section>

      <section class="mb-9">
        <h2 class="section-heading mb-5">Análisis de riesgo con IA</h2>
        <v-card v-if="report.aiAnalysis" class="ai-analysis-card pa-6" elevation="2">
          <div class="ai-analysis-header mb-5">
            <div class="ai-analysis-title">
              <div class="ai-analysis-icon">
                <v-icon color="#5B35D5">mdi-robot-outline</v-icon>
              </div>
              <div>
                <h3>Conclusión del análisis</h3>
                <p>Generado al finalizar las validaciones del reporte.</p>
              </div>
            </div>
            <div class="ai-analysis-metrics">
              <v-chip small label color="#F0EDFF" text-color="#4A2AB8">
                {{ getRiskMeta(report.aiAnalysis.risk_level).label }}
              </v-chip>
              <span v-if="report.aiAnalysis.risk_score != null">{{ report.aiAnalysis.risk_score }}/10</span>
            </div>
          </div>

          <p class="ai-analysis-summary">{{ report.aiAnalysis.summary }}</p>

          <v-row v-if="report.aiAnalysis.findings.length || report.aiAnalysis.recommendations.length" class="mt-2">
            <v-col v-if="report.aiAnalysis.findings.length" cols="12" md="7">
              <h4 class="ai-analysis-subtitle">Hallazgos relevantes</h4>
              <div
                v-for="(finding, index) in report.aiAnalysis.findings"
                :key="`finding-${index}`"
                class="ai-analysis-finding"
              >
                <v-chip x-small label :color="getFindingColor(finding.severity)" text-color="white">
                  {{ finding.severity || 'Información' }}
                </v-chip>
                <div>
                  <strong>{{ finding.category || 'Hallazgo' }}</strong>
                  <p>{{ finding.detail }}</p>
                </div>
              </div>
            </v-col>
            <v-col v-if="report.aiAnalysis.recommendations.length" cols="12" md="5">
              <h4 class="ai-analysis-subtitle">Recomendaciones</h4>
              <ul class="ai-analysis-recommendations">
                <li v-for="(recommendation, index) in report.aiAnalysis.recommendations" :key="`recommendation-${index}`">
                  {{ recommendation }}
                </li>
              </ul>
            </v-col>
          </v-row>

        </v-card>
        <v-card v-else class="ai-analysis-unavailable pa-5" elevation="0">
          <v-icon color="#667085">mdi-information-outline</v-icon>
          <span>El análisis de IA no está disponible para este reporte.</span>
        </v-card>
      </section>

      <section v-if="report.topAlert" class="mb-9">
        <h2 class="section-heading mb-5">Alertas detectadas</h2>
        <v-card class="detected-alert-card pa-5" elevation="2">
          <div class="detected-alert-content">
            <v-icon color="#FF4D00">mdi-alert-outline</v-icon>
            <div>
              <div class="alert-title-line">
                <h3>{{ report.topAlert.title }}</h3>
                <v-chip small label color="#EEF0F4" text-color="#111827" class="status-chip">
                  {{ report.topAlert.severity }}
                </v-chip>
              </div>
              <p>{{ report.topAlert.description }}</p>
            </div>
          </div>
        </v-card>
      </section>

      <section>
        <div class="report-tabs mb-8">
          <button
            v-for="tab in reportTabs"
            :key="tab.value"
            type="button"
            :class="{ active: activeReportTab === tab.value }"
            @click="activeReportTab = tab.value"
          >
            {{ tab.label }}
            <span v-if="tab.pending" class="tab-pending-dot" title="Fuentes pendientes"></span>
          </button>
        </div>

        <div v-if="activeReportTab === 'summary'">
          <v-row>
            <v-col cols="12" lg="6">
              <v-card class="report-section-card pa-7" elevation="2">
                <h2>Datos generales</h2>

                <div
                  v-for="item in report.generalData"
                  :key="item.label"
                  class="general-data-row"
                >
                  <v-icon color="#9CA3AF">{{ item.icon }}</v-icon>
                  <div>
                    <span>{{ item.label }}</span>
                    <strong>{{ item.value }}</strong>
                  </div>
                </div>
              </v-card>
            </v-col>

            <v-col cols="12" lg="6">
              <v-card class="report-section-card pa-7" elevation="2">
                <h2>Indicadores clave</h2>

                <div
                  v-for="indicator in report.keyIndicators"
                  :key="indicator.label"
                  class="key-indicator-row"
                >
                  <div>
                    <span>{{ indicator.label }}</span>
                    <strong :class="indicator.colorClass">{{ indicator.value }}</strong>
                  </div>
                  <v-progress-linear
                    :value="indicator.percent"
                    height="11"
                    rounded
                    background-color="#D0D0D5"
                    :color="indicator.barColor"
                  />
                </div>
              </v-card>
            </v-col>
          </v-row>
        </div>

        <v-card v-else-if="activeReportTab === 'identity'" class="report-section-card pa-7" elevation="2">
          <h2>Validación de identidad</h2>

          <v-row class="mt-6">
            <v-col cols="12" lg="6">
              <div
                v-for="check in report.identityChecks"
                :key="check.label"
                class="identity-check-row"
                :class="{ warning: check.status === 'pending' }"
              >
                <strong>{{ check.label }}</strong>
                <v-icon :color="check.status === 'pending' ? '#FF4D00' : '#00A342'">
                  {{ check.status === 'pending' ? 'mdi-clock-outline' : 'mdi-check-circle-outline' }}
                </v-icon>
              </div>
            </v-col>

            <v-col cols="12" lg="6">
              <div class="sources-box">
                <p>Fuentes consultadas:</p>
                <div
                  v-for="check in report.checks"
                  :key="check.source"
                >
                  <v-icon small :color="check.status === 'pending' ? '#FF4D00' : '#00A342'">
                    {{ check.status === 'pending' ? 'mdi-clock-outline' : 'mdi-check-circle-outline' }}
                  </v-icon>
                  <span>{{ check.source }}</span>
                </div>
              </div>
            </v-col>
          </v-row>

          <v-expansion-panels multiple class="mt-8">
            <v-expansion-panel class="mb-3" v-if="!isCompany">
              <v-expansion-panel-header>CURP (Clave Única de Registro de Población)</v-expansion-panel-header>
              <v-expansion-panel-content>
                <div v-if="report.curpDetail">
                  <p class="mb-1"><strong>CURP:</strong> {{ report.curpDetail.curp }}</p>
                  <p class="mb-1"><strong>Nombre:</strong> {{ report.curpDetail.nombres }} {{ report.curpDetail.primerApellido }} {{ report.curpDetail.segundoApellido }}</p>
                  <p class="mb-1"><strong>Fecha de nacimiento:</strong> {{ report.curpDetail.fechaNacimiento }}</p>
                  <p class="mb-1"><strong>Sexo:</strong> {{ report.curpDetail.sexo }}</p>
                  <p class="mb-1"><strong>Entidad:</strong> {{ report.curpDetail.entidad }} ({{ report.curpDetail.claveEntidad }})</p>
                  <p class="mb-1"><strong>Nacionalidad:</strong> {{ report.curpDetail.nacionalidad }}</p>
                  <p class="mb-0"><strong>Estatus CURP:</strong> {{ report.curpDetail.statusCurp }} — {{ report.curpDetail.descriptionStatusCurp }}</p>
                </div>
                <p v-else class="placeholder-text">Sin coincidencias encontradas.</p>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel class="mb-3">
              <v-expansion-panel-header>Registro Federal de Contribuyentes (RFC)</v-expansion-panel-header>
              <v-expansion-panel-content>
                <p class="mb-1"><strong>Resultado:</strong> {{ report.certificadosSAT.estatus || '—' }}</p>
                <p class="mb-1"><strong>RFC:</strong> {{ report.certificadosSAT.rfc || '—' }}</p>
                <p class="mb-4"><strong>Razón Social:</strong> {{ report.certificadosSAT.razonSocial || '—' }}</p>

                <div v-if="report.certificadosSAT.certificados.length">
                  <h4 class="mb-2">Documentos:</h4>
                  <v-row>
                    <v-col
                      v-for="(cert, index) in report.certificadosSAT.certificados"
                      :key="index"
                      cols="12"
                      md="6"
                      lg="4"
                    >
                      <v-card outlined class="pa-4">
                        <p class="mb-1"><strong>Número de Serie:</strong> {{ cert.numero_serie }}</p>
                        <p class="mb-1"><strong>Estado:</strong> {{ cert.estado }}</p>
                        <p class="mb-1"><strong>Tipo:</strong> {{ cert.tipo }}</p>
                        <p class="mb-1"><strong>Fecha Inicial:</strong> {{ cert.fecha_inicio }}</p>
                        <p class="mb-2"><strong>Fecha Final:</strong> {{ cert.fecha_fin }}</p>
                        <v-btn
                          v-if="cert.certificado"
                          outlined
                          small
                          @click="downloadCertificate(cert.certificado, cert.numero_serie)"
                        >
                          <v-icon left small>mdi-file-download</v-icon>
                          Descargar .Cer
                        </v-btn>
                      </v-card>
                    </v-col>
                  </v-row>
                </div>
                <p v-else class="placeholder-text">Sin certificados encontrados.</p>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel class="mb-3" v-if="!isCompany">
              <v-expansion-panel-header>Registro Nacional de Profesiones (Cédula)</v-expansion-panel-header>
              <v-expansion-panel-content>
                <div v-if="report.cedulaDetalle.length">
                  <v-row>
                    <v-col v-for="(profesion, index) in report.cedulaDetalle" :key="index" cols="12" md="6">
                      <v-card outlined class="pa-4 mb-3">
                        <p class="mb-1 font-weight-bold">{{ profesion.profesion }}</p>
                        <p class="mb-1 grey--text">Cédula: {{ profesion.numCedula }}</p>
                        <p class="mb-1"><strong>Nombre:</strong> {{ profesion.nombre }}</p>
                        <p class="mb-1"><strong>Institución:</strong> {{ profesion.institucion }}</p>
                        <p class="mb-1"><strong>Año de registro:</strong> {{ profesion.fechaRegistro }}</p>
                        <p class="mb-0"><strong>Tipo:</strong> {{ profesion.tipo }}</p>
                      </v-card>
                    </v-col>
                  </v-row>
                </div>
                <p v-else class="placeholder-text">Sin coincidencias encontradas.</p>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel class="mb-3">
              <v-expansion-panel-header>Registro Público de Comercio (SIGER)</v-expansion-panel-header>
              <v-expansion-panel-content>
                <div v-if="report.sigerEntities.length">
                  <v-card v-for="(entidad, index) in report.sigerEntities" :key="index" outlined class="pa-4 mb-4">
                    <p class="mb-1"><strong>Razón Social:</strong> {{ entidad.razon_social || '—' }}</p>
                    <p class="mb-1"><strong>Municipio:</strong> {{ entidad.municipio || '—' }}</p>
                    <p class="mb-1"><strong>Estatus:</strong> {{ entidad.estatus_fme || '—' }}</p>
                    <p class="mb-3"><strong>Tipo de Persona:</strong> {{ entidad.tipo_persona || '—' }}</p>

                    <v-divider class="my-2" />

                    <p class="mb-2 font-weight-bold">Socios ({{ entidad.numero_socios || 0 }}):</p>
                    <v-row v-if="entidad.nombre_socios && entidad.nombre_socios.length">
                      <v-col v-for="(socio, i) in entidad.nombre_socios" :key="i" cols="12" md="6">
                        <v-card outlined class="pa-3">
                          <p class="mb-1"><strong>Nombre:</strong> {{ socio.nombre }} {{ socio.apellido_paterno }} {{ socio.apellido_materno }}</p>
                          <p class="mb-1"><strong>RFC:</strong> {{ socio.rfc || '—' }}</p>
                          <p class="mb-1"><strong>CURP:</strong> {{ socio.curp || '—' }}</p>
                          <p class="mb-1"><strong>Acciones:</strong> {{ socio.acciones || '—' }}</p>
                          <p class="mb-1"><strong>Participación:</strong> {{ getSigerParticipation(entidad, socio) }}</p>
                          <p class="mb-0"><strong>Estatus:</strong> {{ socio.estatus || '—' }}</p>
                        </v-card>
                      </v-col>
                    </v-row>
                    <p v-else class="placeholder-text">Sin socios registrados.</p>
                  </v-card>
                </div>
                <p v-else class="placeholder-text">Sin coincidencias encontradas.</p>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card>

        <v-card v-else-if="activeReportTab === 'blacklists'" class="report-section-card pa-7" elevation="2">
          <h2>Listas Negras</h2>

          <div class="blacklist-summary mb-6">
            <v-chip
              v-for="flag in report.blacklistFlags"
              :key="flag.label"
              small
              label
              :color="flag.value ? '#FFE0E0' : '#EFF7EF'"
              :text-color="flag.value ? '#E60012' : '#00A342'"
              class="mr-2 mb-2"
            >
              <v-icon left x-small :color="flag.value ? '#E60012' : '#00A342'">
                {{ flag.value ? 'mdi-alert-circle-outline' : 'mdi-check-circle-outline' }}
              </v-icon>
              {{ flag.label }}: {{ flag.value ? 'Coincidencia' : 'Sin coincidencia' }}
            </v-chip>
          </div>

          <v-expansion-panels multiple>
            <v-expansion-panel class="mb-3">
              <v-expansion-panel-header>Listas Negras Internacionales (AML)</v-expansion-panel-header>
              <v-expansion-panel-content>
                <div v-if="report.sanctions.length">
                  <v-card
                    v-for="(entry, i) in report.sanctions"
                    :key="i"
                    outlined
                    class="mb-3 pa-4"
                  >
                    <p class="mb-1"><strong>{{ entry.name || 'Sin nombre' }}</strong></p>
                    <p class="mb-1"><strong>Programa:</strong> {{ entry.program || '—' }}</p>
                    <p class="mb-1"><strong>Lista:</strong> {{ entry.list || '—' }}</p>
                    <p class="mb-1"><strong>Nacionalidad:</strong> {{ entry.nationality || '—' }}</p>
                    <p class="mb-0"><strong>Nacimiento:</strong> {{ entry.birth_date || '—' }} — {{ entry.birth_place || '—' }}</p>
                  </v-card>
                </div>
                <p v-else class="placeholder-text">Sin coincidencias encontradas.</p>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel class="mb-3">
              <v-expansion-panel-header>SAT 69 — Contribuyentes No Localizados</v-expansion-panel-header>
              <v-expansion-panel-content>
                <div v-if="report.sat69.length">
                  <v-card v-for="(item, i) in report.sat69" :key="i" outlined class="mb-3 pa-4">
                    <p class="mb-1"><strong>RFC:</strong> {{ item.rfc }}</p>
                    <p class="mb-1"><strong>Razón Social:</strong> {{ item.razon_social }}</p>
                    <p class="mb-1"><strong>Supuesto:</strong> {{ item.supuesto }}</p>
                    <p class="mb-1"><strong>Primera Publicación:</strong> {{ item.fecha_primera_publicacion }}</p>
                    <p class="mb-0"><strong>Entidad Federativa:</strong> {{ item.entidad_federativa }}</p>
                  </v-card>
                </div>
                <p v-else class="placeholder-text">Sin coincidencias encontradas.</p>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel class="mb-3">
              <v-expansion-panel-header>SAT 69B — Contribuyentes Boletinados</v-expansion-panel-header>
              <v-expansion-panel-content>
                <div v-if="report.sat69b.length">
                  <v-card v-for="(item, i) in report.sat69b" :key="i" outlined class="mb-3 pa-4">
                    <p class="mb-1"><strong>RFC:</strong> {{ item.rfc }}</p>
                    <p class="mb-1"><strong>Contribuyente:</strong> {{ item.nombre_contribuyente }}</p>
                    <p class="mb-3"><strong>Situación:</strong> {{ item.situacion_contribuyente }}</p>

                    <p class="mb-1"><strong>Presunción:</strong></p>
                    <ul class="mb-2">
                      <li><strong>Oficio Global:</strong> {{ item.fecha_oficio_global_presuncion || 'N/A' }}</li>
                      <li><strong>Publicación SAT:</strong> {{ item.fecha_publi_sat_presuntos || 'N/A' }}</li>
                      <li><strong>Publicación DOF:</strong> {{ item.fecha_publi_dof_presuntos || 'N/A' }}</li>
                    </ul>

                    <p class="mb-1"><strong>Desvirtuados:</strong></p>
                    <ul class="mb-2">
                      <li><strong>Publicación SAT:</strong> {{ item.fecha_publi_sat_desvirtuados || 'N/A' }}</li>
                      <li><strong>Oficio Global:</strong> {{ item.fecha_oficio_global_desvirtuaron || 'N/A' }}</li>
                      <li><strong>Publicación DOF:</strong> {{ item.fecha_publi_dof_desvirtuaron || 'N/A' }}</li>
                    </ul>

                    <p class="mb-1"><strong>Definitivos:</strong></p>
                    <ul class="mb-2">
                      <li><strong>Oficio Global:</strong> {{ item.fecha_oficio_global_definitivos || 'N/A' }}</li>
                      <li><strong>Publicación SAT:</strong> {{ item.fecha_publi_sat_definitivos || 'N/A' }}</li>
                      <li><strong>Publicación DOF:</strong> {{ item.fecha_publi_dof_definitivos || 'N/A' }}</li>
                    </ul>

                    <p class="mb-1"><strong>Favorable:</strong></p>
                    <ul class="mb-0">
                      <li><strong>Oficio Global:</strong> {{ item.fecha_oficio_global_favorable || 'N/A' }}</li>
                      <li><strong>Publicación SAT:</strong> {{ item.fecha_publi_sat_favorable || 'N/A' }}</li>
                      <li><strong>Publicación DOF:</strong> {{ item.fecha_publi_dof_favorable || 'N/A' }}</li>
                    </ul>
                  </v-card>
                </div>
                <p v-else class="placeholder-text">Sin coincidencias encontradas.</p>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel class="mb-3">
              <v-expansion-panel-header>SAT 74 — Reducción de Multas</v-expansion-panel-header>
              <v-expansion-panel-content>
                <div v-if="report.sat74.length">
                  <v-card v-for="(item, i) in report.sat74" :key="i" outlined class="mb-3 pa-4">
                    <p class="mb-1"><strong>RFC:</strong> {{ item.rfc }}</p>
                    <p class="mb-1"><strong>Razón Social:</strong> {{ item.razon_social }}</p>
                    <p class="mb-1"><strong>Supuesto:</strong> {{ item.supuesto }}</p>
                    <p class="mb-1"><strong>Monto Autorizado:</strong> ${{ Number(item.monto || 0).toLocaleString() }}</p>
                    <p class="mb-0"><strong>Fecha de Autorización:</strong> {{ item.fecha_autorizacion }}</p>
                  </v-card>
                </div>
                <p v-else class="placeholder-text">Sin coincidencias encontradas.</p>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card>

        <v-card v-else-if="activeReportTab === 'idValidation'" class="report-section-card pa-7" elevation="2">
          <h2>Validación de Identificación</h2>

          <v-expansion-panels multiple>
            <v-expansion-panel class="mb-3">
              <v-expansion-panel-header>Extracción de datos de ID (INE)</v-expansion-panel-header>
              <v-expansion-panel-content>
                <div v-if="report.ineFrente && report.ineReverso">
                  <v-row>
                    <v-col cols="12" md="6">
                      <h4 class="font-weight-bold mb-2">Información General</h4>
                      <p class="mb-1"><strong>Nombre:</strong> {{ report.ineFrente.ocr.nombre }}</p>
                      <p class="mb-1"><strong>Apellidos:</strong> {{ report.ineFrente.ocr.apellido_paterno }} {{ report.ineFrente.ocr.apellido_materno }}</p>
                      <p class="mb-1"><strong>Sexo:</strong> {{ report.ineFrente.ocr.sexo }}</p>
                      <p class="mb-1"><strong>CURP:</strong> {{ report.ineFrente.ocr.curp }}</p>
                      <p class="mb-1"><strong>Clave de Elector:</strong> {{ report.ineFrente.ocr.clave }}</p>
                      <p class="mb-1"><strong>Dirección:</strong> {{ report.ineFrente.ocr.calle_numero }}, {{ report.ineFrente.ocr.colonia }}, {{ report.ineFrente.ocr.estado }} {{ report.ineFrente.ocr.codigo_postal }}</p>
                      <p class="mb-1"><strong>Municipio:</strong> {{ report.ineFrente.ocr.municipio }}</p>
                      <p class="mb-1"><strong>Sección:</strong> {{ report.ineFrente.ocr.seccion }}</p>
                      <p class="mb-0"><strong>Vigencia:</strong> {{ report.ineFrente.ocr.vigencia }} (emitida {{ report.ineFrente.ocr.emision }})</p>
                    </v-col>

                    <v-col cols="12" md="6">
                      <h4 class="font-weight-bold mb-2">Validación Visual</h4>
                      <v-chip
                        v-for="(val, key) in report.ineFrente.figuras"
                        :key="key"
                        :color="val ? 'green' : 'red'"
                        dark
                        small
                        class="ma-1"
                      >
                        {{ key }}: {{ val ? 'Sí' : 'No' }}
                      </v-chip>

                      <v-divider class="my-4"></v-divider>

                      <h4 class="font-weight-bold mb-2">Reverso</h4>
                      <p class="mb-1"><strong>Modelo:</strong> {{ report.ineReverso.ocr.model }}</p>
                      <p class="mb-2" style="word-break: break-all;"><strong>MRZ:</strong> {{ report.ineReverso.ocr.mrz }}</p>
                      <v-img v-if="report.ineReverso.url_input" :src="report.ineReverso.url_input" contain max-height="120" class="mb-2"></v-img>
                      <v-img v-if="report.ineReverso.url_output" :src="report.ineReverso.url_output" contain max-height="120"></v-img>
                    </v-col>
                  </v-row>
                </div>
                <p v-else class="placeholder-text">Sin coincidencias encontradas.</p>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel class="mb-3">
              <v-expansion-panel-header>Validación INE (Lista Nominal)</v-expansion-panel-header>
              <v-expansion-panel-content>
                <div v-if="report.listaNominal">
                  <v-row>
                    <v-col cols="12" sm="6">
                      <p class="mb-1"><strong>Estado:</strong> {{ report.listaNominal.estado }}</p>
                      <p class="mb-0">
                        <strong>¿Activa?</strong>
                        <v-chip small :color="report.listaNominal.activa ? 'green' : 'red'" dark>
                          {{ report.listaNominal.activa ? 'Sí' : 'No' }}
                        </v-chip>
                      </p>
                    </v-col>
                    <v-col cols="12" sm="6">
                      <v-alert type="info" dense outlined>{{ report.listaNominal.information }}</v-alert>
                    </v-col>
                  </v-row>

                  <v-divider class="my-4"></v-divider>

                  <h4 class="mb-2 font-weight-bold">Comentarios:</h4>
                  <ul>
                    <li v-for="(comment, i) in report.listaNominal.comentarios" :key="i">{{ comment }}</li>
                  </ul>
                </div>
                <p v-else class="placeholder-text">Sin coincidencias encontradas.</p>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel class="mb-3">
              <v-expansion-panel-header>Geolocalización del Domicilio</v-expansion-panel-header>
              <v-expansion-panel-content>
                <div v-if="report.geoData">
                  <v-row>
                    <v-col cols="12" md="6">
                      <p class="mb-1"><strong>Dirección:</strong> {{ report.geoData.address.label }}</p>
                      <p class="mb-1"><strong>Ciudad:</strong> {{ report.geoData.address.city }}</p>
                      <p class="mb-1"><strong>Estado:</strong> {{ report.geoData.address.state }}</p>
                      <p class="mb-0"><strong>Código Postal:</strong> {{ report.geoData.address.postalCode }}</p>
                    </v-col>
                    <v-col cols="12" md="6">
                      <iframe
                        :src="geoMapUrl"
                        width="100%"
                        height="220"
                        style="border-radius: 12px; border: none;"
                        loading="lazy"
                      ></iframe>
                    </v-col>
                  </v-row>
                </div>
                <p v-else class="placeholder-text">Sin coincidencias encontradas.</p>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card>

        <v-card v-else-if="activeReportTab === 'background'" class="report-section-card pa-7" elevation="2">
          <h2>Antecedentes Judiciales</h2>

          <div
            v-for="(record, index) in report.backgroundRecords"
            :key="index"
            class="background-row"
          >
            <div class="background-icon">
              <v-icon color="#00A342">mdi-check-circle-outline</v-icon>
            </div>
            <div style="flex: 1;">
              <div class="alert-title-line">
                <h3>{{ record.title }}</h3>
                <v-chip small label color="#050417" text-color="white" class="status-chip">
                  {{ record.status }}
                </v-chip>
              </div>
              <p>{{ record.description }}</p>
              <p v-if="record.demandado" class="mb-1"><strong>Demandado:</strong> {{ record.demandado }}</p>
              <span>
                <v-icon x-small color="#6B7280">mdi-calendar-month-outline</v-icon>
                {{ record.date }}
                <template v-if="record.amount">• {{ record.amount }}</template>
              </span>

              <v-expansion-panels v-if="record.acuerdos.length" multiple elevation="0" class="mt-3">
                <v-expansion-panel>
                  <v-expansion-panel-header>Acuerdos</v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <ul>
                      <li v-for="(acuerdo, k) in record.acuerdos" :key="k">
                        <strong>{{ acuerdo.fecha }}:</strong> {{ acuerdo.acuerdo || 'Sin detalle del acuerdo' }}
                      </li>
                    </ul>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>
            <v-btn outlined class="report-nav-button ml-auto" @click="handleViewDetails">
              Ver detalle
            </v-btn>
          </div>

          <p v-if="!report.backgroundRecords.length" class="placeholder-text">
            Sin antecedentes judiciales registrados.
          </p>
        </v-card>

        <v-card v-else-if="activeReportTab === 'labor'" class="report-section-card pa-7" elevation="2">
          <h2>Información Laboral</h2>

          <v-expansion-panels multiple>
            <v-expansion-panel class="mb-3">
              <v-expansion-panel-header>Número de Seguro Social (NSS)</v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-card v-if="report.nss" outlined class="pa-4">
                  <p class="mb-1"><strong>NSS:</strong> {{ report.nss.numero_seguridad_social }}</p>
                  <p class="mb-1"><strong>Nombre:</strong> {{ report.nss.ocr?.datos?.nombre }}</p>
                  <p class="mb-1"><strong>Fecha de emisión:</strong> {{ report.nss.ocr?.datos?.fecha_emision }}</p>
                  <p class="mb-1"><strong>Semanas cotizadas:</strong> {{ report.nss.ocr?.datos?.semanas_cotizadas }}</p>
                  <p class="mb-1"><strong>Semanas descontadas:</strong> {{ report.nss.ocr?.datos?.semanas_descontadas }}</p>
                  <p class="mb-0"><strong>Semanas reintegradas:</strong> {{ report.nss.ocr?.datos?.semanas_reintegradas }}</p>
                </v-card>
                <p v-else class="placeholder-text">No se encontró un número de seguridad social asociado.</p>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel class="mb-3">
              <v-expansion-panel-header>Historial Laboral</v-expansion-panel-header>
              <v-expansion-panel-content>
                <div v-if="report.historialLaboral.length">
                  <v-card v-for="(empleo, i) in report.historialLaboral" :key="i" outlined class="mb-3 pa-4">
                    <p class="mb-1"><strong>Patrón:</strong> {{ empleo.patron }}</p>
                    <p class="mb-1"><strong>Registro Patronal:</strong> {{ empleo.registro_patronal }}</p>
                    <p class="mb-1"><strong>Entidad Federativa:</strong> {{ empleo.entidda_federativa || empleo.entidad_federativa }}</p>
                    <p class="mb-1"><strong>Alta:</strong> {{ empleo.fecha_alta }} <strong class="ml-4">Baja:</strong> {{ empleo.fecha_baja || 'Activo' }}</p>
                    <p class="mb-0"><strong>Salario Base:</strong> {{ empleo.salario_base }}</p>
                  </v-card>
                </div>
                <p v-else class="placeholder-text">No se obtuvieron datos de historial laboral.</p>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- Crédito Infonavit: fuera de alcance, no hay integración real (ni en nufi.js ni nufiCopia.js). Deshabilitado a petición del negocio.
            <v-expansion-panel class="mb-3">
              <v-expansion-panel-header>Crédito Infonavit</v-expansion-panel-header>
              <v-expansion-panel-content>
                <p class="placeholder-text">No disponible por el momento — esta fuente aún no está conectada.</p>
              </v-expansion-panel-content>
            </v-expansion-panel>
            -->
          </v-expansion-panels>
        </v-card>

        <v-card v-else class="report-section-card pa-7" elevation="2">
          <h2>Identidad Digital</h2>

          <v-expansion-panels multiple>
            <v-expansion-panel class="mb-3">
              <v-expansion-panel-header>Análisis y Perfilamiento de Datos de Email y Teléfono</v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-card outlined class="pa-4 mb-4" v-if="report.perfilamientoTelefono">
                  <h4 class="font-weight-bold mb-2">📱 Teléfono</h4>
                  <p class="mb-1"><strong>Número:</strong> {{ report.perfilamientoTelefono.number }}</p>
                  <p class="mb-2">
                    <strong>Válido:</strong>
                    <v-chip small :color="report.perfilamientoTelefono.valid ? 'green' : 'red'" dark>{{ report.perfilamientoTelefono.valid ? 'Sí' : 'No' }}</v-chip>
                    <strong class="ml-4">Temporal:</strong>
                    <v-chip small :color="report.perfilamientoTelefono.disposable ? 'orange' : 'grey'" dark>{{ report.perfilamientoTelefono.disposable ? 'Sí' : 'No' }}</v-chip>
                  </p>
                  <h5 class="mt-3 mb-2">Redes registradas:</h5>
                  <template v-for="(value, platform) in report.perfilamientoTelefono.account_details">
                    <v-chip v-if="value && value.registered" :key="platform" small class="ma-1" color="primary" dark>
                      {{ platform }}
                    </v-chip>
                  </template>
                </v-card>

                <v-card outlined class="pa-4" v-if="report.perfilamientoCorreo">
                  <h4 class="font-weight-bold mb-2">✉️ Correo Electrónico</h4>
                  <p class="mb-1"><strong>Email:</strong> {{ report.perfilamientoCorreo.email }}</p>
                  <p class="mb-2">
                    <strong>Entregable:</strong>
                    <v-chip small :color="report.perfilamientoCorreo.deliverable ? 'green' : 'red'" dark>{{ report.perfilamientoCorreo.deliverable ? 'Sí' : 'No' }}</v-chip>
                  </p>
                  <h5 class="mt-2 mb-2">Dominio:</h5>
                  <p class="mb-1"><strong>Dominio:</strong> {{ report.perfilamientoCorreo.domain_details?.domain }}</p>
                  <v-chip v-if="report.perfilamientoCorreo.domain_details?.valid_mx" small class="ma-1" color="green" dark>MX válido</v-chip>
                  <v-chip v-if="report.perfilamientoCorreo.domain_details?.spf_strict" small class="ma-1" color="green" dark>SPF estricto</v-chip>
                  <v-chip v-if="report.perfilamientoCorreo.domain_details?.dmarc_enforced" small class="ma-1" color="green" dark>DMARC activo</v-chip>
                  <v-chip v-if="report.perfilamientoCorreo.domain_details?.accept_all" small class="ma-1" color="orange" dark>Acepta todos</v-chip>
                  <div v-if="report.perfilamientoCorreo.breach_details?.haveibeenpwned_listed" class="mt-4">
                    <v-alert type="error" dense outlined icon="mdi-alert">
                      Este correo aparece en bases de datos filtradas.
                    </v-alert>
                  </div>
                </v-card>

                <p v-if="!report.perfilamientoTelefono && !report.perfilamientoCorreo" class="placeholder-text">
                  Sin coincidencias encontradas.
                </p>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel class="mb-3">
              <v-expansion-panel-header>Enriquecimiento de datos</v-expansion-panel-header>
              <v-expansion-panel-content>
                <div v-if="report.enrichmentPhones.length">
                  <v-list dense>
                    <v-list-item v-for="(phone, i) in report.enrichmentPhones" :key="i">
                      <v-list-item-content>
                        <v-list-item-title>📞 {{ phone.display_international || phone.display }}</v-list-item-title>
                        <v-list-item-subtitle>Código país: +{{ phone.country_code }} · Número: {{ phone.number }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </div>
                <p v-else class="placeholder-text">Sin coincidencias encontradas.</p>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel class="mb-3">
              <v-expansion-panel-header>Top 5 resultados en Google</v-expansion-panel-header>
              <v-expansion-panel-content>
                <div v-if="report.searchResults.length">
                  <v-card v-for="(item, i) in report.searchResults" :key="i" outlined class="mb-3 pa-4">
                    <a :href="item.link" target="_blank" class="font-weight-bold">{{ item.title }}</a>
                    <p v-if="item.displayed_link" class="mb-1 mt-1 text-caption">{{ item.displayed_link }}</p>
                    <p v-if="item.snippet" class="mb-1">{{ item.snippet }}</p>
                    <p class="mb-0 mt-1 text-caption">{{ item.source }} <span v-if="item.date">• {{ item.date }}</span></p>

                    <div v-if="item.richAttributes.length" class="mt-2">
                      <p class="text-caption font-weight-bold mb-1">Detalles:</p>
                      <ul>
                        <li v-for="(attr, j) in item.richAttributes" :key="'attr-' + j">{{ attr.name }}: {{ attr.value }}</li>
                      </ul>
                    </div>

                    <div v-if="item.sitelinks.length" class="mt-2">
                      <p class="text-caption font-weight-bold mb-1">Sitios relacionados:</p>
                      <ul>
                        <li v-for="(link, j) in item.sitelinks" :key="'site-' + j">
                          <a :href="link.link" target="_blank">{{ link.title }}</a>
                        </li>
                      </ul>
                    </div>
                  </v-card>
                </div>
                <p v-else class="placeholder-text">Sin coincidencias encontradas.</p>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel class="mb-3">
              <v-expansion-panel-header>Top 5 noticias en Google</v-expansion-panel-header>
              <v-expansion-panel-content>
                <div v-if="report.newsResults.length">
                  <v-row v-for="(item, i) in report.newsResults" :key="i" class="mb-3">
                    <v-col cols="12">
                      <v-card outlined class="pa-4">
                        <v-row dense>
                          <v-col v-if="item.thumbnail" cols="3">
                            <v-img :src="item.thumbnail" height="80" cover class="rounded"></v-img>
                          </v-col>
                          <v-col :cols="item.thumbnail ? 9 : 12">
                            <a :href="item.link" target="_blank" class="font-weight-bold">{{ item.title }}</a>
                            <p class="mb-0 mt-1 text-caption">{{ item.source }} <span v-if="item.date">• {{ item.date }}</span></p>
                            <p v-if="item.snippet" class="mb-0 mt-2">{{ item.snippet }}</p>
                          </v-col>
                        </v-row>
                      </v-card>
                    </v-col>
                  </v-row>
                </div>
                <p v-else class="placeholder-text">Sin coincidencias encontradas.</p>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card>
      </section>
    </div>
  </v-container>
</template>

<script>
import moment from 'moment';

export default {
  name: 'DueDiligenceReport',
  data() {
    return {
      loading: false,
      error: null,
      activeReportTab: 'summary',
      report: null,
      downloadingPdf: false,
    };
  },
  computed: {
    selectLanguage() {
      return this.$store.state.language;
    },
    isCompany() {
      return this.report?.subject?.type === 'Empresa';
    },
    recommendationMeta() {
      const recommendations = {
        bajo: {
          title: 'Perfil de riesgo bajo',
          description: 'Los resultados disponibles no muestran señales de riesgo elevado. Revisa el detalle antes de tomar una decisión.',
          icon: 'mdi-check-circle-outline',
          color: '#008A38',
          cardClass: 'recommendation-card--low',
        },
        medio: {
          title: 'Revisión adicional sugerida',
          description: 'Se identificaron elementos que conviene validar antes de continuar con la relación comercial.',
          icon: 'mdi-alert-circle-outline',
          color: '#B45309',
          cardClass: 'recommendation-card--medium',
        },
        alto: {
          title: 'Revisión reforzada necesaria',
          description: 'El reporte contiene señales de riesgo elevado. Analiza los hallazgos y aplica tus políticas internas.',
          icon: 'mdi-alert-octagon-outline',
          color: '#D92D20',
          cardClass: 'recommendation-card--high',
        },
      };

      return recommendations[this.report?.riskLevelRaw] || {
        title: 'Evaluación pendiente',
        description: 'Todavía no existen datos suficientes para presentar una orientación de riesgo.',
        icon: 'mdi-clock-outline',
        color: '#667085',
        cardClass: 'recommendation-card--pending',
      };
    },
    reportTabs() {
      if (!this.report) return [];

      const tabs = [
        { label: 'Resumen', value: 'summary' },
        { label: 'Validación de Identidad', value: 'identity' },
        { label: 'Listas Negras', value: 'blacklists' },
      ];

      if (!this.isCompany) {
        tabs.push({ label: 'Validación de Identificación', value: 'idValidation' });
      }

      tabs.push({ label: 'Antecedentes Judiciales', value: 'background' });

      if (!this.isCompany) {
        tabs.push({ label: 'Información Laboral', value: 'labor' });
      }

      tabs.push({ label: 'Identidad Digital', value: 'digital' });

      return tabs.map((tab) => ({ ...tab, pending: this.isTabPending(tab.value) }));
    },
    geoMapUrl() {
      const pos = this.report?.geoData?.position;
      if (!pos) return '';
      return `https://www.openstreetmap.org/export/embed.html?bbox=${pos.lng - 0.005},${pos.lat - 0.005},${pos.lng + 0.005},${pos.lat + 0.005}&layer=mapnik&marker=${pos.lat},${pos.lng}`;
    },
  },
  created() {
    this.getReport();
  },
  methods: {
    isTabPending(tabValue) {
      if (!this.report?.checks?.length) return false;

      const TAB_SOURCES = {
        identity: ['curp', 'validacionEmpresa', 'certificadosSAT', 'SIGER', 'cedula'],
        blacklists: ['listasNegras', 'OFAC', 'sat69', 'sat69B', 'sat74'],
        idValidation: ['ineFrente', 'ineReverso', 'listaNominal', 'geoData'],
        background: ['getAntecedentesJudiciales'],
        labor: ['nss'],
        digital: ['perfilamientoTelefono', 'perfilamientoCorreo', 'top5Noticias', 'top5results', 'enriquecimientoNombre'],
      };

      const sources = TAB_SOURCES[tabValue];
      if (!sources) return false;

      return this.report.checks.some((c) => sources.includes(c.source) && c.status === 'pending');
    },
    getReport() {
      const idReport = this.$route.params.idReport;
      const type = this.$route.query.type === 'empresa' ? 'Empresa' : 'Persona';

      this.loading = true;
      this.error = null;
      const endpoint = type === 'Empresa'
        ? `/check/report/${idReport}`
        : `/check/reportPerson/${idReport}`;

      DB.get(`${URI}/api/v1/${this.selectLanguage}${endpoint}`, {
        headers: { Authorization: `Bearer ${this.$store.state.sToken}` },
      })
        .then((response) => {
          this.report = this.mapReportDetail(response.data.data, type);
          this.activeReportTab = 'summary';
        })
        .catch((error) => {
          this.error = error.response?.data?.message || 'No se pudo cargar el reporte';
        })
        .finally(() => {
          this.loading = false;
        });
    },
    handleRefreshReport() {
      this.getReport();
    },
    handleBackToQueries() {
      this.$router.push('/dueDiligence');
    },
    getRiskMeta(riskLevel) {
      switch (riskLevel) {
        case 'alto':
          return {
            label: 'Riesgo alto', scoreClass: 'score-high',
            riskColor: '#FFE0E0', riskTextColor: '#E60012',
          };
        case 'medio':
          return {
            label: 'Riesgo medio', scoreClass: 'score-medium',
            riskColor: '#FFE6C9', riskTextColor: '#FF4D00',
          };
        case 'bajo':
          return {
            label: 'Riesgo bajo', scoreClass: 'score-low',
            riskColor: '#D9FAE4', riskTextColor: '#00A342',
          };
        default:
          return {
            label: 'Sin evaluar', scoreClass: '',
            riskColor: '#E7E9EE', riskTextColor: '#465266',
          };
      }
    },
    mapReportDetail(raw, type) {
      moment.locale(this.selectLanguage === 'sp' ? 'es' : 'en');
      const isCompany = type === 'Empresa';
      const riskMeta = this.getRiskMeta(raw.risk_level);
      const checks = raw.checks || [];
      const done = checks.filter((c) => c.status === 'completed' || c.status === 'skipped').length;

      return {
        sId: raw.sId,
        subject: {
          name: isCompany
            ? raw.razon_social || 'Sin razón social'
            : [raw.nombres, raw.apellido_paterno, raw.apellido_materno].filter(Boolean).join(' ') || 'Sin nombre',
          type: isCompany ? 'Empresa' : 'Persona Física',
          identifier: raw.rfc || raw.curp || '—',
        },
        generatedAt: raw.created_at ? moment(raw.created_at).format('DD MMM YYYY, hh:mm A') : '—',
        status: raw.status,
        riskLevelRaw: raw.risk_level || null,
        progress: { total: checks.length, done },
        riskScore: raw.risk_score != null ? String(raw.risk_score) : '—',
        riskLevelLabel: riskMeta.label,
        aiAnalysis: this.parseRiskAnalysis(raw.ia_response),
        riskIndicators: [
          { label: 'Confiabilidad crediticia', value: null },
          { label: 'Historial limpio', value: null },
          { label: 'Validación de identidad', value: null },
        ],
        topAlert: this.buildTopAlert(raw),
        generalData: this.buildGeneralData(raw, isCompany),
        keyIndicators: this.buildKeyIndicators(raw, isCompany),
        backgroundRecords: this.buildBackgroundRecords(raw.children?.judicial || []),
        identityChecks: this.buildIdentityChecks(raw, isCompany),
        checks,
        blacklistFlags: isCompany
          ? [{ label: 'Sanciones', value: !!raw.has_sanction_match }]
          : [
            { label: 'Sanciones', value: !!raw.has_sanction_match },
            { label: 'PEP', value: !!raw.has_pep_match },
            { label: 'Watchlist', value: !!raw.has_watchlist_match },
            { label: 'Lista criminal', value: !!raw.has_crimelist_match },
          ],
        sanctions: raw.children?.sanctions || [],
        sat69: (raw.data?.sat69?.data) || [],
        sat69b: (raw.data?.sat69B?.data) || [],
        sat74: (raw.data?.sat74?.data) || [],
        ineFrente: raw.data?.ineFrente?.data || null,
        ineReverso: raw.data?.ineReverso?.data || null,
        listaNominal: raw.data?.listaNominal?.data?.[0] || null,
        geoData: raw.data?.geoData?.data?.items?.[0] || null,
        perfilamientoTelefono: raw.data?.perfilamientoTelefono?.data || null,
        perfilamientoCorreo: raw.data?.perfilamientoCorreo?.data || null,
        searchResults: (raw.children?.news || []).filter((n) => n.kind === 'resultado').map((n) => ({
          ...n,
          displayed_link: n.raw?.displayed_link || null,
          snippet: n.raw?.snippet || null,
          richAttributes: n.raw?.rich_snippet?.top?.attributes || [],
          sitelinks: n.raw?.sitelinks?.inline || [],
        })),
        newsResults: (raw.children?.news || []).filter((n) => n.kind === 'noticia').map((n) => ({
          ...n,
          thumbnail: n.raw?.thumbnail || null,
          snippet: n.raw?.snippet || null,
        })),
        nss: raw.data?.nss?.data || null,
        historialLaboral: raw.data?.nss?.data?.ocr?.empleos || [],
        enrichmentPhones: raw.data?.enriquecimientoNombre?.data?.query?.phones || [],
        curpDetail: raw.data?.curp?.data?.curpdata?.[0] || null,
        certificadosSAT: {
          estatus: raw.data?.validacionEmpresa?.data?.estatus || null,
          rfc: raw.data?.certificadosSAT?.data?.rfc || null,
          razonSocial: raw.data?.certificadosSAT?.data?.razon_social || null,
          certificados: raw.data?.certificadosSAT?.data?.certificados || [],
        },
        cedulaDetalle: raw.data?.cedula?.data || [],
        sigerEntities: raw.data?.SIGER?.data || [],
      };
    },
    parseRiskAnalysis(rawAnalysis) {
      if (!rawAnalysis) return null;

      try {
        const analysis = typeof rawAnalysis === 'string' ? JSON.parse(rawAnalysis) : rawAnalysis;
        if (!analysis || typeof analysis !== 'object' || !analysis.summary) return null;

        return {
          summary: analysis.summary,
          risk_level: analysis.risk_level || null,
          risk_score: analysis.risk_score ?? null,
          findings: Array.isArray(analysis.findings) ? analysis.findings.filter((finding) => finding?.detail) : [],
          recommendations: Array.isArray(analysis.recommendations) ? analysis.recommendations.filter(Boolean) : [],
        };
      } catch (error) {
        return null;
      }
    },
    getFindingColor(severity) {
      const normalizedSeverity = String(severity || '').toLowerCase();
      if (normalizedSeverity === 'alto' || normalizedSeverity === 'alta') return '#D92D20';
      if (normalizedSeverity === 'medio' || normalizedSeverity === 'media') return '#DC6803';
      return '#027A48';
    },
    getSigerParticipation(entity, partner) {
      const partners = Array.isArray(entity?.nombre_socios) ? entity.nombre_socios : [];
      const actions = partners.map((item) => Number(String(item.acciones ?? '').replace(/,/g, '')));
      const totalActions = actions.reduce((sum, value) => (Number.isFinite(value) ? sum + value : sum), 0);
      const partnerActions = Number(String(partner?.acciones ?? '').replace(/,/g, ''));

      if (!totalActions || !Number.isFinite(partnerActions)) return 'No disponible';
      const percentage = (partnerActions / totalActions) * 100;
      return `${Number(percentage.toFixed(2))}%`;
    },
    buildTopAlert(raw) {
      const sanction = raw.children?.sanctions?.[0];
      if (sanction) {
        return {
          title: sanction.name || 'Coincidencia en lista de sanciones',
          severity: 'Alta',
          description: [sanction.list, sanction.program].filter(Boolean).join(' — ') || 'Coincidencia detectada en listas de sanciones (OFAC/AML)',
        };
      }
      const judicial = raw.children?.judicial?.[0];
      if (judicial) {
        return {
          title: judicial.tipo || 'Antecedente judicial detectado',
          severity: 'Media',
          description: [judicial.actor, judicial.juzgado].filter(Boolean).join(' — ') || judicial.entidad || 'Registro judicial encontrado',
        };
      }
      return null;
    },
    buildGeneralData(raw, isCompany) {
      if (isCompany) {
        return [
          { label: 'Razón social', value: raw.razon_social || '—', icon: 'mdi-office-building-outline' },
          { label: 'RFC', value: raw.rfc || 'No proporcionado', icon: 'mdi-file-document-outline' },
          { label: 'Marca / Nombre comercial', value: raw.marca || '—', icon: 'mdi-tag-outline' },
          { label: 'Dominio', value: raw.dominio || '—', icon: 'mdi-web' },
          { label: 'Situación fiscal', value: raw.situacion_contribuyente || 'Sin información', icon: 'mdi-file-chart-outline' },
        ];
      }
      return [
        {
          label: 'Nombre completo',
          value: [raw.nombres, raw.apellido_paterno, raw.apellido_materno].filter(Boolean).join(' ') || '—',
          icon: 'mdi-account-outline',
        },
        { label: 'CURP', value: raw.curp || 'No proporcionado', icon: 'mdi-file-document-outline' },
        { label: 'RFC', value: raw.rfc || '—', icon: 'mdi-file-document-outline' },
        { label: 'Teléfono', value: raw.phone || '—', icon: 'mdi-phone-outline' },
        { label: 'Correo electrónico', value: raw.correo_electronico || '—', icon: 'mdi-email-outline' },
        { label: 'Fecha de nacimiento', value: raw.fecha_nacimiento ? moment(raw.fecha_nacimiento).format('DD MMM YYYY') : '—', icon: 'mdi-cake-variant-outline' },
        { label: 'Sexo', value: raw.sexo || '—', icon: 'mdi-gender-male-female' },
        { label: 'Estado de nacimiento', value: raw.estado_nacimiento || '—', icon: 'mdi-map-marker-outline' },
      ];
    },
    buildKeyIndicators(raw, isCompany) {
      const boolIndicator = (label, value) => ({
        label,
        value: value ? 'Sí' : 'No',
        percent: value ? 100 : 0,
        barColor: value ? '#050417' : '#9CA3AF',
        colorClass: value ? 'green-text' : '',
      });
      const countIndicator = (label, count, badWhenPositive = true) => {
        const n = count || 0;
        const isBad = badWhenPositive && n > 0;
        return {
          label,
          value: n != null ? String(n) : '—',
          percent: Math.min(n * 20, 100),
          barColor: isBad ? '#FF4D00' : '#050417',
          colorClass: isBad ? 'orange-text' : 'green-text',
        };
      };

      if (isCompany) {
        return [
          {
            label: 'Certificados SAT',
            value: `${raw.certificados_sat_activos || 0}/${raw.certificados_sat_total || 0} activos`,
            percent: raw.certificados_sat_total ? Math.round((raw.certificados_sat_activos / raw.certificados_sat_total) * 100) : 0,
            barColor: '#050417',
            colorClass: 'green-text',
          },
          countIndicator('Socios registrados (SIGER)', raw.siger_numero_socios, false),
          countIndicator('Garantías RUG', raw.rug_numero_operaciones, false),
          countIndicator('Marcas registradas (IMPI)', raw.impi_total_marcas, false),
          countIndicator('Antecedentes judiciales', raw.antecedentes_judiciales_total),
        ];
      }
      return [
        countIndicator('Antecedentes judiciales', raw.antecedentes_judiciales_total),
        boolIndicator('Lista nominal activa', raw.lista_nominal_activa),
        boolIndicator('Cédula profesional', raw.tiene_cedula_profesional),
        boolIndicator('Teléfono válido', raw.telefono_valido),
        boolIndicator('Correo entregable', raw.correo_deliverable),
      ];
    },
    buildBackgroundRecords(judicial) {
      return judicial.map((entry) => ({
        title: entry.tipo || 'Antecedente judicial',
        status: 'Registrado',
        description: [entry.actor, entry.juzgado].filter(Boolean).join(' — ') || entry.entidad || 'Sin detalle adicional',
        date: entry.fecha || '—',
        amount: '',
        demandado: entry.raw?.demandado || null,
        acuerdos: entry.raw?.acuerdos || [],
      }));
    },
    buildIdentityChecks(raw, isCompany) {
      if (isCompany) {
        return [
          { label: 'RFC validado', status: raw.rfc_estatus ? 'completed' : 'pending' },
          { label: 'Situación fiscal obtenida', status: raw.situacion_contribuyente ? 'completed' : 'pending' },
          { label: 'Certificados SAT verificados', status: raw.certificados_sat_total != null ? 'completed' : 'pending' },
        ];
      }
      return [
        { label: 'CURP válido', status: raw.curp_valido ? 'completed' : 'pending' },
        { label: 'INE vigente', status: raw.ine_vigente ? 'completed' : 'pending' },
        { label: 'Lista nominal activa', status: raw.lista_nominal_activa ? 'completed' : 'pending' },
        { label: 'Cédula profesional', status: raw.tiene_cedula_profesional ? 'completed' : 'pending' },
      ];
    },
    handleShareReport() {
      navigator.clipboard.writeText(window.location.href)
        .then(() => this.mixSuccess('Link del reporte copiado al portapapeles'))
        .catch(() => this.mixError('No se pudo copiar el link'));
    },
    handleDownloadReport(mode = 'resumen') {
      if (!this.report || this.downloadingPdf) return;

      this.downloadingPdf = true;
      const endpoint = this.isCompany
        ? `/check/report/${this.report.sId}/pdf`
        : `/check/reportPerson/${this.report.sId}/pdf`;

      DB.get(`${URI}/api/v1/${this.selectLanguage}${endpoint}?mode=${mode}`, {
        headers: { Authorization: `Bearer ${this.$store.state.sToken}` },
        responseType: 'blob',
      })
        .then((response) => {
          const blob = new Blob([response.data], { type: 'application/pdf' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `reporte-${this.report.sId}${mode === 'extenso' ? '-extenso' : ''}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        })
        .catch(async (error) => {
          let message = 'No se pudo generar el PDF';
          if (error.response?.data instanceof Blob) {
            try {
              const text = await error.response.data.text();
              message = JSON.parse(text)?.message || message;
            } catch (e) { /* keep default message */ }
          } else {
            message = error.response?.data?.message || message;
          }
          this.mixError(message);
        })
        .finally(() => {
          this.downloadingPdf = false;
        });
    },
    handleViewDetails() {},
    downloadCertificate(base64Data, fileName = 'certificado') {
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/x-x509-ca-cert' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${fileName}.cer`;
      link.click();
      URL.revokeObjectURL(link.href);
    },
  },
};
</script>

<style scoped>
.identity-page {
  min-height: 100vh;
  background: #f7f8fa;
  color: #0c0d10;
}

.report-view {
  min-height: 100vh;
}

.report-top-actions,
.report-header,
.report-title-line,
.report-actions,
.risk-card-top,
.risk-meter div,
.alert-title-line,
.key-indicator-row div {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.report-title-line,
.alert-title-line {
  justify-content: flex-start;
  flex-wrap: wrap;
}

.report-nav-button {
  height: 48px !important;
  border-color: #d8d9df;
  border-radius: 8px;
  background: white;
  font-family: 'montserrat-bold';
  font-size: 16px;
  color: #111111;
  text-transform: none;
}

.report-header h1 {
  font-family: 'montserrat-bold';
  font-size: 40px;
  line-height: 1.15;
  margin-bottom: 12px;
  color: #0c0d10;
}

.report-header p {
  font-family: 'montserrat-semibold';
  font-size: 21px;
  color: #465266;
  margin-bottom: 0;
}

.entity-chip {
  font-family: 'montserrat-bold';
}

.risk-score-card,
.recommendation-card,
.detected-alert-card,
.report-section-card {
  border-radius: 16px;
}

.risk-score-card {
  min-height: 530px;
  background: linear-gradient(135deg, #08c653, #00a86b);
  color: white;
}

.risk-card-top {
  margin-bottom: 88px;
}

.risk-card-top p {
  font-family: 'montserrat-bold';
  font-size: 21px;
  margin-bottom: 18px;
}

.risk-score {
  font-family: 'montserrat-bold';
  font-size: 96px;
  line-height: 1;
  margin-bottom: 22px;
}

.risk-score span {
  font-size: 40px;
  font-family: 'montserrat-semibold';
}

.risk-level-chip {
  font-family: 'montserrat-bold';
}

.risk-shield {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 106px;
  height: 106px;
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.25);
}

.risk-meter {
  margin-bottom: 24px;
}

.risk-meter span,
.risk-meter strong {
  font-family: 'montserrat-bold';
  font-size: 18px;
}

.risk-meter div {
  margin-bottom: 10px;
}

.risk-meter strong.no-data {
  font-family: 'montserrat-semibold';
  font-size: 14px;
  opacity: 0.75;
}

.recommendation-card {
  min-height: 530px;
  background: #effcf3;
}

.recommendation-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  margin-bottom: 62px;
  border-radius: 16px;
  background: #d9fae4;
}

.recommendation-card h2 {
  margin-bottom: 52px;
  font-family: 'montserrat-bold';
  font-size: 25px;
}

.recommendation-card p {
  margin-bottom: 56px;
  font-family: 'montserrat-semibold';
  font-size: 18px;
  line-height: 1.45;
  color: #2f3b4f;
}


.ai-analysis-card {
  border: 1px solid #e7e3ff;
  background: #fff;
}

.ai-analysis-header,
.ai-analysis-title,
.ai-analysis-metrics,
.ai-analysis-finding,
.ai-analysis-unavailable {
  display: flex;
  align-items: center;
}

.ai-analysis-header {
  justify-content: space-between;
  gap: 16px;
}

.ai-analysis-title {
  gap: 14px;
}

.ai-analysis-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: #f0edff;
}

.ai-analysis-title h3,
.ai-analysis-subtitle {
  margin: 0;
  font-family: 'montserrat-bold';
  color: #172033;
}

.ai-analysis-title p {
  margin: 4px 0 0;
  font-family: 'montserrat-semibold';
  font-size: 13px;
  color: #667085;
}

.ai-analysis-metrics {
  gap: 10px;
  font-family: 'montserrat-bold';
  color: #4a2ab8;
  white-space: nowrap;
}

.ai-analysis-summary {
  margin: 0;
  font-family: 'montserrat-semibold';
  font-size: 16px;
  line-height: 1.6;
  color: #344054;
}

.ai-analysis-subtitle {
  margin-bottom: 14px;
  font-size: 16px;
}

.ai-analysis-finding {
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 12px;
}

.ai-analysis-finding strong {
  font-family: 'montserrat-bold';
  font-size: 14px;
  color: #344054;
}

.ai-analysis-finding p {
  margin: 3px 0 0;
  font-family: 'montserrat-semibold';
  font-size: 14px;
  line-height: 1.45;
  color: #667085;
}

.ai-analysis-recommendations {
  margin: 0;
  padding-left: 21px;
  font-family: 'montserrat-semibold';
  font-size: 14px;
  line-height: 1.6;
  color: #344054;
}

.ai-analysis-recommendations li + li {
  margin-top: 8px;
}

.ai-analysis-unavailable {
  gap: 10px;
  border: 1px solid #e4e7ec;
  background: #f9fafb;
  font-family: 'montserrat-semibold';
  color: #475467;
}

.section-heading {
  font-family: 'montserrat-bold';
  font-size: 25px;
  color: #0c0d10;
}

.detected-alert-card {
  background: #fff8ed;
  border: 1px solid #ffedd1;
}

.detected-alert-content {
  display: flex;
  align-items: flex-start;
  gap: 18px;
}

.detected-alert-content h3,
.background-row h3 {
  margin-bottom: 8px;
  font-family: 'montserrat-bold';
  font-size: 22px;
}

.detected-alert-content p,
.background-row p {
  margin-bottom: 0;
  font-family: 'montserrat-semibold';
  font-size: 18px;
  color: #2f3b4f;
}

.report-tabs {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 4px;
  border-radius: 18px;
  background: #e7e9ee;
}

.report-tabs button {
  position: relative;
  height: 39px;
  padding: 0 16px;
  border: 0;
  border-radius: 16px;
  background: transparent;
  font-family: 'montserrat-bold';
  font-size: 16px;
  color: #111111;
  cursor: pointer;
  white-space: nowrap;
}

.report-tabs button.active {
  background: white;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
}

.tab-pending-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  margin-left: 6px;
  border-radius: 50%;
  background: #FF4D00;
  vertical-align: middle;
}

.report-section-card {
  min-height: 438px;
  background: white;
}

.report-section-card h2 {
  margin-bottom: 58px;
  font-family: 'montserrat-bold';
  font-size: 25px;
}

.placeholder-text {
  font-family: 'montserrat-semibold';
  font-size: 18px;
  color: #9CA3AF;
  margin-bottom: 0;
}

.general-data-row {
  display: flex;
  align-items: flex-start;
  gap: 18px;
  margin-bottom: 26px;
}

.general-data-row span,
.key-indicator-row span {
  display: block;
  margin-bottom: 6px;
  font-family: 'montserrat-semibold';
  font-size: 18px;
  color: #465266;
}

.general-data-row strong,
.key-indicator-row strong {
  font-family: 'montserrat-bold';
  font-size: 20px;
  color: #0c0d10;
}

.key-indicator-row {
  margin-bottom: 28px;
}

.key-indicator-row div {
  margin-bottom: 12px;
}

.green-text {
  color: #00a342 !important;
}

.orange-text {
  color: #ff4d00 !important;
}

.background-row {
  display: flex;
  align-items: flex-start;
  gap: 20px;
  padding: 24px 0;
  border-bottom: 1px solid #edf0f3;
}

.background-row:last-child {
  border-bottom: 0;
}

.background-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 54px;
  height: 54px;
  border-radius: 12px;
  background: #d9fae4;
}

.background-row span {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  font-family: 'montserrat-semibold';
  font-size: 16px;
  color: #6b7280;
}

.identity-check-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 60px;
  margin-bottom: 20px;
  padding: 0 16px;
  border-radius: 12px;
  background: #effcf3;
  font-family: 'montserrat-bold';
  font-size: 18px;
}

.identity-check-row.warning {
  background: #fff8ed;
}

.sources-box {
  min-height: 298px;
  padding: 26px;
  border-radius: 12px;
  background: #f5f6f8;
}

.sources-box p {
  margin-bottom: 18px;
  font-family: 'montserrat-semibold';
  font-size: 18px;
  color: #465266;
}

.sources-box div {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
  font-family: 'montserrat-semibold';
  font-size: 18px;
  color: #0c0d10;
}

@media (max-width: 960px) {
  .identity-page {
    padding-left: 20px !important;
    padding-right: 20px !important;
  }

  .report-header,
  .report-actions,
  .risk-card-top,
  .background-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .report-header h1 {
    font-size: 32px;
  }

  .report-header p,
  .recommendation-card p,
  .sources-box div {
    font-size: 17px;
  }

  .report-actions,
  .report-nav-button {
    width: 100%;
  }

  .risk-score-card,
  .recommendation-card {
    min-height: auto;
  }

  .risk-card-top,
  .recommendation-icon,
  .recommendation-card h2,
  .recommendation-card p {
    margin-bottom: 28px;
  }

  .risk-score {
    font-size: 72px;
  }

  .report-tabs {
    display: flex;
    width: 100%;
    overflow-x: auto;
  }
}

/* Fresher AI shared visual language */
.identity-page {
  width: 100%;
  max-width: 1740px !important;
  margin: 0 auto;
  padding: 28px 40px 32px !important;
  font-family: 'montserrat-regular';
}

.report-header h1 {
  font-size: 28px;
  line-height: 1.2;
}

.report-header p {
  font-family: 'montserrat-regular';
  font-size: 14px;
  color: #718096;
}

.risk-score-card,
.recommendation-card,
.ai-analysis-card {
  min-height: auto;
  border: 1px solid #e2e7ed !important;
  border-radius: 12px !important;
  box-shadow: 0 3px 12px rgba(30, 47, 70, 0.04) !important;
}

.recommendation-card {
  min-height: 100%;
  background: #f7f9fb;
}

.recommendation-card--low {
  background: #f1fbf4;
}

.recommendation-card--medium {
  background: #fffaeb;
}

.recommendation-card--high {
  background: #fff4f2;
}

.recommendation-card--pending {
  background: #f7f9fb;
}

.recommendation-icon {
  width: 48px;
  height: 48px;
  margin-bottom: 28px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.75);
}

.recommendation-card h2 {
  margin-bottom: 16px;
  font-size: 19px;
}

.recommendation-card p {
  margin-bottom: 0;
  font-family: 'montserrat-regular';
  font-size: 14px;
  line-height: 1.65;
}

@media (max-width: 960px) {
  .identity-page {
    padding: 24px !important;
  }

  .report-header h1 {
    font-size: 26px;
  }

  .report-header p,
  .recommendation-card p {
    font-size: 13px;
  }
}

@media (max-width: 600px) {
  .identity-page {
    padding: 20px 16px 28px !important;
  }
}
</style>
